import type { ReactElement, ReactNode } from 'react';
import { formatChf } from '@agentselly/ui';
import { AppLink } from '~/components/AppLink';

export function V3Page(props: { readonly children: ReactNode }): ReactElement {
  return (
    <div className="v3-overview">
      <div className="stack">{props.children}</div>
    </div>
  );
}

export function PageHead(props: {
  readonly title: string;
  readonly sub?: string;
  readonly end?: ReactNode;
  readonly dataWidget?: string;
  readonly 'data-widget'?: string;
}): ReactElement {
  return (
    <div
      className="hrow"
      data-widget={props['data-widget'] ?? props.dataWidget}
    >
      <div className="head">
        <h1>{props.title}</h1>
        {props.sub ? <p className="mu">{props.sub}</p> : null}
      </div>
      {props.end}
    </div>
  );
}

export function Kpi(props: {
  readonly value: ReactNode;
  readonly label: string;
  readonly delta?: number | null;
  readonly dataWidget?: string;
  readonly 'data-widget'?: string;
}): ReactElement {
  const d = props.delta;
  return (
    <div
      className="kpi"
      data-widget={props['data-widget'] ?? props.dataWidget}
    >
      <div className="n num">{props.value}</div>
      <div className="l">{props.label}</div>
      {d ? (
        <div className={`d ${d > 0 ? 'up' : 'down'}`}>
          {d > 0 ? '+' : ''}
          {d}
        </div>
      ) : null}
    </div>
  );
}

export function Privacy(props: { readonly children: ReactNode }): ReactElement {
  return <p className="note xs mu">{props.children}</p>;
}

export function CardLink(props: {
  readonly to?: string;
  readonly children: ReactNode;
}): ReactElement | null {
  if (!props.to) return null;
  return (
    <AppLink
      to={props.to}
      className="btn ghost"
    >
      {props.children}
    </AppLink>
  );
}

export function fmtChf(value?: number | string | null): string {
  const n = Number(value);
  return value == null || value === '' || !Number.isFinite(n) || n === 0
    ? '–'
    : formatChf(n, { maximumFractionDigits: 0 });
}

export function fmtDate(value?: string | number | Date | null): string {
  if (value == null || value === '') return '–';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '–'
    : d.toLocaleDateString('de-CH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
}

export function fmtTime(value?: string | number | Date | null): string {
  const d = new Date(value ?? NaN);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
}

export function Bars(props: {
  readonly rows: ReadonlyArray<{
    readonly label: string;
    readonly value: number;
    readonly text?: string;
  }>;
  readonly max?: number;
}): ReactElement {
  const max = props.max ?? Math.max(1, ...props.rows.map((r) => r.value));
  return (
    <div className="bars">
      {props.rows.map((r) => (
        <div
          className="bar"
          key={r.label}
        >
          <span className="lb">{r.label}</span>
          <span className="v num">{r.text ?? r.value}</span>
          <span
            className="trk"
            aria-hidden="true"
          >
            <span
              className="fil"
              style={{ width: `${Math.min(100, (r.value / max) * 100)}%` }}
            />
          </span>
        </div>
      ))}
    </div>
  );
}

export function LineChart(props: {
  readonly values: readonly number[];
  readonly labels: readonly [string, string];
  readonly title: string;
}): ReactElement {
  const w = 600;
  const h = 160;
  const max = Math.max(1, ...props.values);
  const step = props.values.length > 1 ? w / (props.values.length - 1) : w;
  const pts = props.values.map(
    (v, i) =>
      `${(i * step).toFixed(1)},${(h - (v / max) * (h - 8) - 4).toFixed(1)}`,
  );
  const line = pts.join(' ');
  return (
    <figure className="chart">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label={props.title}
      >
        <line
          className="gl"
          x1="0"
          x2={w}
          y1={h - 1}
          y2={h - 1}
        />
        {pts.length > 1 ? (
          <>
            <polygon
              className="ar"
              points={`0,${h} ${line} ${w},${h}`}
            />
            <polyline
              className="ln"
              points={line}
            />
          </>
        ) : null}
      </svg>
      <div className="ax">
        <span>{props.labels[0]}</span>
        <span>{props.labels[1]}</span>
      </div>
    </figure>
  );
}

export function Steps(props: {
  readonly done: readonly boolean[];
  readonly label: string;
}): ReactElement {
  const reached = props.done.filter(Boolean).length;
  return (
    <div className="stp">
      <div
        className="stp-t"
        aria-hidden="true"
      >
        {props.done.map((on, i) => (
          <span
            key={i}
            style={{ display: 'contents' }}
          >
            {i > 0 ? <b className={on ? 'on' : ''} /> : null}
            <i
              className={`${on ? 'on' : ''} ${
                on && i === reached - 1 ? 'cur' : ''
              }`}
            />
          </span>
        ))}
      </div>
      <span className="stp-n">{props.label}</span>
    </div>
  );
}
